import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Icon,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Tag,
  TagCloseButton,
  TagLabel,
  Text,
  Textarea,
  VStack,
  useColorModeValue,
} from "@chakra-ui/react";
import { WarningTwoIcon } from "@chakra-ui/icons";
import { Mail, Plus } from "lucide-react";
import type { BenefitsPendencyPayload } from "../../../../lib/api";
import type { MissionItem } from "../../../../types/onboarding";

export interface PendencyModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Missões do candidato vindas do starter-pack (detail.missions). */
  missions: MissionItem[];
  candidateName?: string | null;
  onSubmit: (payload: BenefitsPendencyPayload) => Promise<void>;
  isSubmitting: boolean;
}

/** Missões que o RH pode reabrir no starter-pack — o reopen só aceita
 * WAITING_RH ou COMPLETED. */
const isReopenable = (m: MissionItem): boolean => m.completed || m.waiting_rh;

/** Por padrão pré-seleciona a missão de benefícios (demanda original). */
const isBenefitsMission = (m: MissionItem): boolean => m.title.toLowerCase().includes("benef");

/** Labels enviados pelo candidato — sugestões rápidas de pendência. */
const suggestionLabels = (m: MissionItem | null): string[] => {
  if (!m || !m.submission) return [];
  const labels: string[] = [];
  if (m.submission.type === "form" || m.submission.type === "mixed") {
    m.submission.fields.forEach(f => { if (f.label) labels.push(f.label); });
  }
  if (m.submission.type === "files" || m.submission.type === "mixed") {
    m.submission.files.forEach(f => { if (f.label) labels.push(f.label); });
  }
  return Array.from(new Set(labels));
};

export const PendencyModal: React.FC<PendencyModalProps> = ({ isOpen, onClose, missions, candidateName, onSubmit, isSubmitting }) => {
  const [missionId, setMissionId] = useState<number | null>(null);
  const [items, setItems] = useState<string[]>([]);
  const [itemInput, setItemInput] = useState("");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState("");

  const bg         = useColorModeValue("white", "#0f1f3a");
  const inputBg    = useColorModeValue("gray.50", "#0a1628");
  const labelColor = useColorModeValue("gray.500", "gray.400");
  const warnBg     = useColorModeValue("orange.50", "rgba(251,146,60,0.08)");
  const warnBorder = useColorModeValue("orange.200", "rgba(251,146,60,0.3)");

  const reopenable = useMemo(() => missions.filter(isReopenable), [missions]);

  useEffect(() => {
    if (isOpen) {
      const preferred = reopenable.find(isBenefitsMission) ?? reopenable[0] ?? null;
      setMissionId(preferred?.id ?? null);
      setItems([]);
      setItemInput("");
      setNotes("");
      setError("");
    }
  }, [isOpen, reopenable]);

  const selectedMission = reopenable.find(m => m.id === missionId) ?? null;
  const suggestions = suggestionLabels(selectedMission);

  const addItem = (value: string) => {
    const v = value.trim();
    if (!v) return;
    setItems(prev => (prev.some(i => i.toLowerCase() === v.toLowerCase()) ? prev : [...prev, v]));
    setItemInput("");
    if (error) setError("");
  };

  const removeItem = (v: string) => setItems(prev => prev.filter(i => i !== v));

  const handleSubmit = () => {
    if (missionId == null) { setError("Selecione a missão."); return; }
    if (items.length === 0) { setError("Informe ao menos um item de pendência."); return; }
    onSubmit({ mission_definition_id: missionId, items, notes: notes.trim() || null });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="md">
      <ModalOverlay backdropFilter="blur(4px)" bg="blackAlpha.400" />
      <ModalContent borderRadius="2xl" bg={bg}>
        <ModalHeader fontSize="md" fontWeight={600} pb={1}>Cobrar pendências de benefícios</ModalHeader>
        <ModalCloseButton />
        <ModalBody pt={2} pb={4}>
          <VStack align="stretch" spacing={4}>
            <Box p={3} borderRadius="lg" bg={warnBg} borderWidth="1px" borderColor={warnBorder}>
              <HStack spacing={2} mb={1}>
                <WarningTwoIcon color="orange.400" boxSize="12px" />
                <Text fontSize="xs" fontWeight={700} color="orange.500">Reabre a missão e envia o e-mail de cobrança</Text>
              </HStack>
              <Text fontSize="xs" color={labelColor}>
                {candidateName ? `${candidateName}: ` : ""}a missão selecionada volta a ficar disponível no portal
                do candidato com a lista de pendências, e ele recebe um e-mail para corrigir.
              </Text>
            </Box>

            <FormControl isInvalid={!!error && missionId == null}>
              <FormLabel mb={1} fontSize="sm" fontWeight={500}>Missão</FormLabel>
              <Select
                size="sm" bg={inputBg} borderRadius="lg"
                value={missionId ?? ""}
                onChange={(e) => { setMissionId(Number(e.target.value) || null); setError(""); }}
                isDisabled={isSubmitting || reopenable.length === 0}
              >
                {reopenable.length === 0 && <option value="">Nenhuma missão concluída/aguardando RH</option>}
                {reopenable.map(m => (
                  <option key={m.id} value={m.id}>{m.title}{m.waiting_rh ? " (aguardando RH)" : ""}</option>
                ))}
              </Select>
              <FormErrorMessage fontSize="xs" mt={1}>{error}</FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={!!error && items.length === 0}>
              <FormLabel mb={1} fontSize="sm" fontWeight={500}>Itens pendentes</FormLabel>
              {items.length > 0 && (
                <HStack spacing={2} flexWrap="wrap" mb={2}>
                  {items.map(item => (
                    <Tag key={item} size="sm" colorScheme="orange" borderRadius="full">
                      <TagLabel maxW="220px">{item}</TagLabel>
                      <TagCloseButton onClick={() => removeItem(item)} isDisabled={isSubmitting} />
                    </Tag>
                  ))}
                </HStack>
              )}
              <HStack spacing={2}>
                <Input
                  placeholder="Ex.: Comprovante de residência"
                  size="sm" bg={inputBg} borderRadius="lg"
                  value={itemInput}
                  onChange={(e) => setItemInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addItem(itemInput); } }}
                  isDisabled={isSubmitting}
                />
                <Button size="sm" variant="outline" leftIcon={<Icon as={Plus} boxSize="13px" />} onClick={() => addItem(itemInput)} isDisabled={isSubmitting}>
                  Adicionar
                </Button>
              </HStack>
              {suggestions.length > 0 && (
                <HStack spacing={2} flexWrap="wrap" mt={2}>
                  {suggestions.map(label => (
                    <Button
                      key={label}
                      size="xs" variant="ghost" colorScheme="blue" borderRadius="full" px={2}
                      onClick={() => addItem(label)}
                      isDisabled={isSubmitting || items.some(i => i.toLowerCase() === label.toLowerCase())}
                    >
                      + {label}
                    </Button>
                  ))}
                </HStack>
              )}
              <FormErrorMessage fontSize="xs" mt={1}>{error}</FormErrorMessage>
            </FormControl>

            <FormControl>
              <FormLabel mb={1} fontSize="sm" fontWeight={500}>Observação (opcional)</FormLabel>
              <Textarea
                placeholder="Mensagem adicional que irá no e-mail ao candidato"
                size="sm" bg={inputBg} borderRadius="lg" rows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                isDisabled={isSubmitting}
              />
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter gap={2} pt={2}>
          <Button variant="ghost" size="sm" onClick={onClose} isDisabled={isSubmitting}>Cancelar</Button>
          <Button
            colorScheme="orange"
            size="sm"
            onClick={handleSubmit}
            isLoading={isSubmitting}
            loadingText="Enviando..."
            borderRadius="lg"
            leftIcon={<Icon as={Mail} boxSize="13px" />}
            isDisabled={reopenable.length === 0}
          >
            Reabrir e cobrar
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};